import { FIN_CATEGORY_BY_KEY, isRealEstate, TaxKey } from '#dictionary'
import { FilingStatus } from '#dictionary/filingStatuses'
import { getFilingStatus } from '#dictionary/filingStatuses'
import { IncomeTaxationType } from '#dictionary/finCategories'
import type { Region } from '#dictionary/regions'
import { getStandardDeduction } from '#dictionary/standardDeductions/getStandardDeduction'
import ProfileService from '#profile/ProfileService'

import type { TimelineRecord } from '../timeline/TimelineRecord'
import type {
  TaxCalculationResult,
  TotalTaxesCalculationResult,
} from '../types'
import { zeroData } from './const'
import type {
  ITaxCalculator,
  NoTaxVariety,
  TaxVarietyWithFilingStatus,
  TaxVarietyWithFilingStatusAndRegion,
} from './taxes'
import RealEstateTax from './taxes/RealEstateTax'
import {
  calculateChildTaxCredit,
  calculateTaxableSocialSecurity,
  SOCIAL_SECURITY_THRESHOLD,
} from './taxHelpers'
import type {
  AnnualTaxesReport,
  CalculatedValues,
  IncomeByTaxationType,
} from './types'

// IRC §1211(b) — net capital loss deduction limit for individuals
const MIN_CAPITAL_LOSS_DEDUCTION = 3000
// IRC §1402(b) — minimum net earnings from self-employment subject to SE tax
const SE_TAX_THRESHOLD = 400

// TODO: Social Security tax 6.2% - hard-code. No brackets needed for SE
// TODO: #must calculate taxes per person if they are such. Count only members
// that are part of the filing status
// TODO: #must AMT taxes calculation
// TODO: #must implement state deductions
// TODO: #must state brackets calculation
// TODO: #must properly display federal LTCG tax brackets and rates in report
// missunderstanding of LTCG and Federal taxes. extra calculations on total value

// 1. Aggregate Provisional Income Base
class AnnualTaxes {
  private medicareTax: ITaxCalculator<NoTaxVariety>
  private socialSecurityTax: ITaxCalculator<NoTaxVariety>
  private medicareSETax: ITaxCalculator<NoTaxVariety>
  private socialSecuritySETax: ITaxCalculator<NoTaxVariety>
  private federalTax: ITaxCalculator<TaxVarietyWithFilingStatus>
  private stateTax: ITaxCalculator<TaxVarietyWithFilingStatusAndRegion>
  private capitalGainsTax: ITaxCalculator<TaxVarietyWithFilingStatus>
  private additionalMedicareTax: ITaxCalculator<TaxVarietyWithFilingStatus>
  private netInvestmentIncomeTax: ITaxCalculator<TaxVarietyWithFilingStatus>
  private alternativeMinimumTax: ITaxCalculator<NoTaxVariety>
  private realEstateTax: RealEstateTax

  private incomeByTaxationType: IncomeByTaxationType
  private taxes: TaxCalculationResult[] = []

  public readonly filingStatus: FilingStatus
  public readonly region: Region
  public readonly standardDeduction: number
  private readonly numberOfChildrenUnder17: number

  constructor(
    public year: number,
    public timelineRecords: readonly TimelineRecord[],
    public taxCalculators: Record<
      Exclude<TaxKey, TaxKey.TaxStateProperty>,
      ITaxCalculator
    >,
    profile: ProfileService,
    realEstateTaxCalculator: RealEstateTax
  ) {
    this.filingStatus = getFilingStatus(profile)
    this.region = profile.person.region
    this.standardDeduction = getStandardDeduction(
      this.filingStatus,
      year,
      profile
    )
    this.numberOfChildrenUnder17 =
      profile.childrenQualifiedForTaxCreditCount(year)
    this.medicareTax = this.taxCalculators[TaxKey.TaxMedicare]
    this.socialSecurityTax = this.taxCalculators[TaxKey.TaxSocialSecurity]
    this.medicareSETax = this.taxCalculators[TaxKey.TaxMedicareSE]
    this.socialSecuritySETax = this.taxCalculators[TaxKey.TaxSocialSecuritySE]
    this.federalTax = this.taxCalculators[TaxKey.TaxFederalIncome]
    this.capitalGainsTax = this.taxCalculators[TaxKey.TaxCapitalGains]
    this.stateTax = this.taxCalculators[TaxKey.TaxStateIncome]
    this.additionalMedicareTax =
      this.taxCalculators[TaxKey.TaxAdditionalMedicare]
    this.netInvestmentIncomeTax =
      this.taxCalculators[TaxKey.TaxNetInvestmentIncome]
    this.alternativeMinimumTax =
      this.taxCalculators[TaxKey.TaxAlternativeMinimum]
    this.realEstateTax = realEstateTaxCalculator

    this.incomeByTaxationType = Object.fromEntries(
      Object.values(IncomeTaxationType).map(incomeType => [
        incomeType,
        this.calculateIncomeByTaxationType(incomeType),
      ])
    ) as IncomeByTaxationType
  }

  // TODO: #should add warning if some income type is not found
  public calculate(): TotalTaxesCalculationResult {
    const calculations: Partial<CalculatedValues> = {}
    // STEP 1. Aggregate Provisional Income Base
    calculations.netProfitSelfEmployment =
      this.incomeByTaxationType.selfEmploymentGrossRevenue -
      zeroData.businessExpenses
    calculations.capitalLossDeduction = Math.min(
      zeroData.capitalLosses,
      MIN_CAPITAL_LOSS_DEDUCTION
    )
    calculations.domesticIncome =
      this.incomeByTaxationType.w2Wages +
      calculations.netProfitSelfEmployment +
      this.incomeByTaxationType.shortTermCapitalGains +
      this.incomeByTaxationType.longTermCapitalGains +
      calculations.capitalLossDeduction +
      this.incomeByTaxationType.qualifiedDividends +
      this.incomeByTaxationType.interestOrdinaryDividends +
      this.incomeByTaxationType.pensionIraDistributions +
      this.incomeByTaxationType.rentalIncome +
      this.incomeByTaxationType.k1Income +
      this.incomeByTaxationType.otherIncome

    calculations.foreignIncome =
      zeroData.foreignWages +
      zeroData.foreignBankInterest +
      zeroData.foreignCapitalGains +
      zeroData.foreignRentalRoyalty +
      zeroData.foreignPassiveIncome

    calculations.provisionalIncomeBase =
      calculations.domesticIncome + calculations.foreignIncome

    // STEP 2. Determine Taxable Portion of Social Security Benefits
    calculations.provisionalIncome =
      calculations.provisionalIncomeBase -
      zeroData.studentLoanInterestDeduction +
      this.incomeByTaxationType.socialSecurityBenefits * 0.5

    calculations.socialSecurityTaxationThreshold50Percent =
      SOCIAL_SECURITY_THRESHOLD[this.filingStatus][50]
    calculations.socialSecurityTaxationThreshold85Percent =
      SOCIAL_SECURITY_THRESHOLD[this.filingStatus][85]

    calculations.taxableSocialSecurity = calculateTaxableSocialSecurity(
      calculations.provisionalIncome,
      this.incomeByTaxationType.socialSecurityBenefits,
      this.filingStatus
    )

    // STEP 3. Calculate Net Profit and Taxes from Self-Employment
    calculations.netEarningsForSeTax =
      calculations.netProfitSelfEmployment * this.getSEEarningsMultiplier()

    const socialSecurityWageBaseLimit =
      this.socialSecurityTax.getTaxableThreshold(this.year)

    if (calculations.netEarningsForSeTax >= SE_TAX_THRESHOLD) {
      const seTax = this.socialSecuritySETax.calculate(
        Math.min(
          calculations.netEarningsForSeTax,
          Math.max(
            0,
            socialSecurityWageBaseLimit - this.incomeByTaxationType.w2Wages
          )
        ),
        this.year
      )
      calculations.seSocialSecurityTax = seTax.totalTax

      const seMedicareTax = this.medicareSETax.calculate(
        calculations.netEarningsForSeTax,
        this.year
      )
      calculations.seMedicareTax = seMedicareTax.totalTax

      calculations.selfEmploymentTax =
        calculations.seSocialSecurityTax + calculations.seMedicareTax

      this.taxes.push(seTax)
      this.taxes.push(seMedicareTax)

      calculations.deductiblePortionSeTax = calculations.selfEmploymentTax * 0.5
    } else {
      calculations.deductiblePortionSeTax = 0
      calculations.seSocialSecurityTax = 0
      calculations.seMedicareTax = 0
      calculations.selfEmploymentTax = 0
    }

    // STEP 3b: FICA taxes on W2 wages
    const w2SocialSecurityTax = this.socialSecurityTax.calculate(
      this.incomeByTaxationType.w2Wages,
      this.year
    )
    calculations.w2SocialSecurityTax = w2SocialSecurityTax.totalTax

    const w2MedicareTax = this.medicareTax.calculate(
      this.incomeByTaxationType.w2Wages,
      this.year
    )
    calculations.w2MedicareTax = w2MedicareTax.totalTax

    calculations.w2FicaTax =
      w2SocialSecurityTax.totalTax + w2MedicareTax.totalTax

    this.taxes.push(w2SocialSecurityTax)
    this.taxes.push(w2MedicareTax)

    // STEP 4.  Calculate Key Income Milestones (Gross Income, AGI, MAGI)
    calculations.grossIncome =
      calculations.provisionalIncomeBase + calculations.taxableSocialSecurity
    calculations.adjustedGrossIncome =
      calculations.grossIncome -
      calculations.deductiblePortionSeTax -
      zeroData.hsaDeduction -
      zeroData.studentLoanInterestDeduction
    calculations.modifiedAdjustedIncome =
      calculations.adjustedGrossIncome + zeroData.studentLoanInterestDeduction

    // STEP 5. Calculate Federal Taxable Income Tax Liability
    calculations.standardDeduction = this.standardDeduction
    calculations.chosenDeduction =
      zeroData.totalItemizedDeductions > calculations.standardDeduction
        ? zeroData.totalItemizedDeductions
        : calculations.standardDeduction

    calculations.taxableIncomeBeforeQBI = Math.max(
      0,
      calculations.adjustedGrossIncome - calculations.chosenDeduction
    )

    calculations.qbiDeduction = 0 // TODO: #must implement

    calculations.finalFederalTaxableIncome =
      calculations.taxableIncomeBeforeQBI - calculations.qbiDeduction

    calculations.ordinaryTaxableIncome =
      calculations.finalFederalTaxableIncome -
      this.incomeByTaxationType.longTermCapitalGains -
      this.incomeByTaxationType.qualifiedDividends

    const federalTax = this.federalTax.calculate(
      calculations.ordinaryTaxableIncome,
      this.year,
      { filingStatus: this.filingStatus }
    )
    calculations.federalTaxOrdinaryIncome = federalTax.totalTax

    this.taxes.push(federalTax)

    // TODO: #should @max how to display capital gain tax trackets information?
    const capitalGainsTax = this.capitalGainsTax.calculate(
      calculations.finalFederalTaxableIncome,
      this.year,
      { filingStatus: this.filingStatus }
    )

    calculations.federalTaxLtcg = Math.max(
      0,
      capitalGainsTax.totalTax - calculations.federalTaxOrdinaryIncome
    )
    if (calculations.federalTaxLtcg > 0) {
      this.taxes.push(capitalGainsTax)
    }

    // STEP 6: Calculate State Tax Liability

    calculations.stateAgi =
      calculations.adjustedGrossIncome +
      zeroData.stateAdditions -
      zeroData.stateSubtractions

    calculations.stateTaxableIncome =
      calculations.stateAgi - zeroData.stateDeduction

    const stateTax = this.stateTax.calculate(
      calculations.stateTaxableIncome,
      this.year,
      {
        filingStatus: this.filingStatus,
        region: this.region,
      }
    )
    calculations.stateTax = stateTax.totalTax
    this.taxes.push(stateTax)

    // STEP 7: Calculate Federal Surtaxes & Other Taxes
    calculations.totalEarnedIncome =
      this.incomeByTaxationType.w2Wages + calculations.netProfitSelfEmployment

    const additionalMedicareTaxResult = this.additionalMedicareTax.calculate(
      calculations.totalEarnedIncome,
      this.year,
      { filingStatus: this.filingStatus }
    )
    calculations.additionalMedicareTaxThreshold =
      this.additionalMedicareTax.getTaxableThreshold(this.year, {
        filingStatus: this.filingStatus,
      })
    calculations.additionalMedicareTaxRate =
      this.additionalMedicareTax.getTaxRate(this.year, {
        filingStatus: this.filingStatus,
      })
    calculations.additionalMedicareTax = additionalMedicareTaxResult.totalTax

    this.taxes.push(additionalMedicareTaxResult)

    calculations.netCapitalGains =
      this.incomeByTaxationType.shortTermCapitalGains +
      this.incomeByTaxationType.longTermCapitalGains -
      zeroData.capitalLosses
    calculations.netInvestmentIncome =
      this.incomeByTaxationType.interestOrdinaryDividends +
      this.incomeByTaxationType.qualifiedDividends +
      this.incomeByTaxationType.rentalIncome +
      this.incomeByTaxationType.k1Income +
      calculations.netCapitalGains

    calculations.netInvestmentIncomeTaxThreshold =
      this.netInvestmentIncomeTax.getTaxableThreshold(this.year, {
        filingStatus: this.filingStatus,
      })

    calculations.netInvestmentIncomeBase = Math.min(
      calculations.netInvestmentIncome,
      Math.max(
        0,
        calculations.modifiedAdjustedIncome -
          calculations.netInvestmentIncomeTaxThreshold
      )
    )

    calculations.netInvestmentIncomeTaxRate =
      this.netInvestmentIncomeTax.getTaxRate(this.year, {
        filingStatus: this.filingStatus,
      })

    const netInvestmentIncomeTax = this.netInvestmentIncomeTax.calculate(
      calculations.netInvestmentIncomeBase,
      this.year,
      { filingStatus: this.filingStatus }
    )
    calculations.netInvestmentIncomeTaxResult = netInvestmentIncomeTax.totalTax
    this.taxes.push(netInvestmentIncomeTax)
    // STEP 8: Calculate Alternative Minimum Tax
    calculations.amti =
      calculations.finalFederalTaxableIncome +
      this.incomeByTaxationType.amtAddBacks

    // TODO: #must implement AMT brackets
    const alternativeMinimumTax = this.alternativeMinimumTax.calculate(
      calculations.amti,
      this.year
    )
    calculations.tentativeMinimumTax = alternativeMinimumTax.totalTax
    calculations.totalFederalIncomeTax =
      calculations.federalTaxOrdinaryIncome + calculations.federalTaxLtcg
    calculations.alternativeMinimumTaxResult = Math.max(
      0,
      calculations.tentativeMinimumTax - calculations.totalFederalIncomeTax
    )

    if (calculations.alternativeMinimumTaxResult > 0) {
      this.taxes.push(alternativeMinimumTax)
    }

    // STEP 9: Calculate Child Tax Credit
    calculations.numberOfChildrenUnder17 = this.numberOfChildrenUnder17
    calculations.childTaxCredit = calculateChildTaxCredit(
      this.numberOfChildrenUnder17,
      calculations.modifiedAdjustedIncome,
      zeroData.ctcPhaseoutThreshold
    )

    calculations.totalTaxCreditsBeforeCapping =
      calculations.childTaxCredit + zeroData.childAndDependentCareCredit

    // STEP 10: Calculate Real Estate Taxes

    const realEstateRecords = this.timelineRecords.filter(record => {
      const category = FIN_CATEGORY_BY_KEY.get(record.categoryKey)
      return category !== undefined && isRealEstate(category)
    })

    realEstateRecords.forEach(record => {
      const realEstateTax = this.realEstateTax.calculate(record, this.year)
      // TODO: #should add information about the related real estate
      this.taxes.push(realEstateTax)
    })

    // STEP 11: Calculate Total Taxes Liability

    calculations.totalFederalTaxBeforeCredits =
      calculations.federalTaxOrdinaryIncome +
      calculations.federalTaxLtcg +
      calculations.w2SocialSecurityTax +
      calculations.w2MedicareTax +
      calculations.seSocialSecurityTax +
      calculations.seMedicareTax +
      calculations.additionalMedicareTax +
      calculations.netInvestmentIncomeTaxResult +
      calculations.alternativeMinimumTaxResult

    calculations.totalTaxCredits = Math.min(
      calculations.totalTaxCreditsBeforeCapping,
      calculations.totalFederalTaxBeforeCredits
    )

    calculations.finalFederalTaxLiability = Math.max(
      0,
      calculations.totalFederalTaxBeforeCredits - calculations.totalTaxCredits
    )

    calculations.finalStateTaxLiability =
      calculations.stateTax + zeroData.otherStateTaxes
    calculations.finalTotalTaxLiability =
      calculations.finalFederalTaxLiability +
      calculations.finalStateTaxLiability

    const report: AnnualTaxesReport = {
      ...zeroData,
      ...this.incomeByTaxationType,
      ...calculations,
    } as AnnualTaxesReport

    return {
      year: this.year,
      filingStatus: this.filingStatus,
      region: this.region,
      totalTax: calculations.finalTotalTaxLiability,
      taxes: this.taxes,
      federalTaxCreditAmount: calculations.totalTaxCredits,
      report,
    }
  }

  private calculateIncomeByTaxationType(
    incomeType: IncomeTaxationType
  ): number {
    return this.timelineRecords.reduce((acc, record) => {
      const category = FIN_CATEGORY_BY_KEY.get(record.categoryKey)
      if (!category) return acc

      if (category.i_taxationType === incomeType) {
        return acc + record.value
      }

      return acc
    }, 0)
  }

  private getSEEarningsMultiplier(): number {
    const medicareTaxRate = this.medicareTax.getTaxRate(this.year)
    const socialSecurityTaxRate = this.socialSecurityTax.getTaxRate(this.year)
    return 1 - (medicareTaxRate + socialSecurityTaxRate)
  }
}

export default AnnualTaxes
